import OnboardingChoiceGroup from '@/onboarding/components/OnboardingChoiceGroup'
import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Heading,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  OrgType,
  orgTypePresets,
  sharedGovernanceVocabularies,
} from '../orgTypes'

interface Props {
  orgType: OrgType
  onChange(orgType: OrgType): void
  rolesHelp: boolean
  onRolesHelpChange(rolesHelp: boolean): void
}

// The "Shared governance" card stands for several models that differ only by
// their vocabulary; the vocabulary is picked right under it.
const SHARED = 'SharedGovernance'
const isShared = (type: OrgType) =>
  (sharedGovernanceVocabularies as readonly OrgType[]).includes(type)

export default function OrgSetupStepModel({
  orgType,
  onChange,
  rolesHelp,
  onRolesHelpChange,
}: Props) {
  const { t } = useTranslation()

  const options = useMemo(
    () => [
      {
        value: OrgType.Empty,
        label: t('OrgTypeSelect.Empty'),
        description: t('OrgTypeSelect.Empty_desc'),
      },
      {
        value: OrgType.Classic,
        label: t('OrgTypeSelect.Classic'),
        description: t('OrgTypeSelect.Classic_desc'),
      },
      {
        value: SHARED,
        label: t('OrgTypeSelect.SharedGovernance'),
        description: t('OrgTypeSelect.SharedGovernance_desc'),
      },
    ],
    [t]
  )

  const handleCard = (value: string) => {
    if (value !== SHARED) return onChange(value as OrgType)
    // Keep the vocabulary already picked, otherwise preselect the first one
    if (!isShared(orgType)) onChange(sharedGovernanceVocabularies[0])
  }

  return (
    <VStack spacing={5} align="stretch">
      <Heading as="h1" size="md">
        {t('OrgSetupModal.heading')}
      </Heading>
      <Text>{t('OrgSetupModal.subheading')}</Text>

      <OnboardingChoiceGroup
        ariaLabel={t('OrgSetupModal.orgType')}
        options={options}
        value={isShared(orgType) ? SHARED : orgType}
        onChange={handleCard}
        columns={1}
      />

      {isShared(orgType) && (
        <VStack spacing={2} align="stretch">
          <ButtonGroup
            size="sm"
            isAttached
            variant="outline"
            role="radiogroup"
            aria-label={t('OrgSetupModal.vocabulary')}
          >
            {sharedGovernanceVocabularies.map((type) => (
              <Button
                key={type}
                role="radio"
                aria-checked={orgType === type}
                colorScheme={orgType === type ? 'blue' : undefined}
                variant={orgType === type ? 'solid' : 'outline'}
                onClick={() => onChange(type)}
              >
                {t(orgTypePresets[type].labelKey)}
              </Button>
            ))}
          </ButtonGroup>
          <Text fontSize="sm" color="gray.500">
            {t(orgTypePresets[orgType].helpKey)}
          </Text>
        </VStack>
      )}

      {orgType !== OrgType.Empty && (
        <FormControl display="flex" alignItems="center">
          <Switch
            id="org-setup-roles-help"
            isChecked={rolesHelp}
            onChange={(e) => onRolesHelpChange(e.target.checked)}
            mr={3}
          />
          <FormLabel htmlFor="org-setup-roles-help" mb={0}>
            {t('OrgSetupModal.rolesHelp')}
          </FormLabel>
        </FormControl>
      )}
    </VStack>
  )
}
